import { useContext, useMemo, useState, useCallback, useEffect } from 'react';
import {
  Stack,
  Button,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Box,
  LinearProgress,
  Alert,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarQuickFilter,
  useGridApiRef,
  GridPaginationModel,
} from '@mui/x-data-grid';
import { useQuery } from '@apollo/client';
import { DashboardContext } from '../DashboardContext';
import { GET_DIAGNOSIS_REPORT_QUERY, GET_PATIENT_REPORT_QUERY, GET_EARLIEST_PATIENT_DATE_QUERY } from '../queries';
import DateRangePicker from 'shared-components/components/DateRangePicker/DateRangePicker';
import dayjs, { Dayjs } from 'dayjs';
import { CurrentAppConfig } from 'op-pages/RO/Careplan/AppConfig';
import { useHistory } from 'react-router-dom';
import { isDemo } from 'op-utils';
import { PermissionType } from 'op-enums';
import {
  filterDataByReportType,
  applyDateFilter,
  expandSiteGroupData,
  generateColumns,
  applySorting,
  generateCSVReport,
  ReportType,
} from '../Utils/reportUtils';
import { ProgressiveLoading } from '../Utils/progressiveLoading';

const LOCAL_ENV = import.meta.env.VITE_LOCAL_ENV;

const CustomToolbar = () => (
  <GridToolbarContainer sx={{ justifyContent: 'space-between' }}>
    <Stack direction="row" spacing={1}>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
    </Stack>
    <GridToolbarQuickFilter />
  </GridToolbarContainer>
);

const dataGridStyles = {
  '& .MuiDataGrid-cell:focus': {
    outline: 0,
  },
  '& .MuiDataGrid-toolbarContainer .MuiButton-root': {
    border: 'none',
  },
  '& .MuiDataGrid-toolbarContainer .MuiButtonBase-root': {
    border: 'none',
  },
  '& .MuiDataGrid-footerContainer': {
    alignItems: 'center',
  },
  '& .MuiTablePagination-root': {
    alignItems: 'center',
  },
  '& .MuiTablePagination-displayedRows, & .MuiTablePagination-selectLabel': {
    margin: 0,
    alignSelf: 'center',
    transform: 'translateY(-2px)',
  },
};

const Reports = () => {
  const { rawCareplanData, navToPage, tenantConfig, prescriptionsLoading, userData } = useContext(DashboardContext);
  const history = useHistory();
  const apiRef = useGridApiRef();
  const [reportType, setReportType] = useState<ReportType>(ReportType.PRESCRIPTION);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([dayjs().subtract(1, 'month'), dayjs()]);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 20,
  });

  const areDatesValid = useCallback((dateRange: [Dayjs | null, Dayjs | null]): boolean => {
    const [startDate, endDate] = dateRange;
    return !!(startDate && endDate && startDate.isValid() && endDate.isValid());
  }, []);

  const { data: earliestDateData } = useQuery(GET_EARLIEST_PATIENT_DATE_QUERY, {
    fetchPolicy: 'cache-first',
    errorPolicy: 'ignore',
  });

  // Patient report date restriction - dynamic based on earliest patient + 2 days
  const PATIENT_MIN_DATE = useMemo(() => {
    if (earliestDateData?.earliestPatientDate) {
      return dayjs(earliestDateData.earliestPatientDate).add(2, 'day');
    }
    return null;
  }, [earliestDateData]);

  const getAdjustedDateRange = useCallback(
    (dateRange: [Dayjs | null, Dayjs | null]): [Dayjs | null, Dayjs | null] => {
      const [startDate, endDate] = dateRange;

      if (startDate && startDate.isValid() && startDate.isBefore(PATIENT_MIN_DATE)) {
        return [PATIENT_MIN_DATE, endDate];
      }

      return dateRange;
    },
    [reportType, PATIENT_MIN_DATE],
  );

  const shouldShowPatientDateWarning = useMemo(() => {
    return (
      reportType === ReportType.PATIENT &&
      dateRange[0] &&
      dateRange[0].isValid() &&
      dateRange[0].isBefore(PATIENT_MIN_DATE)
    );
  }, [reportType, dateRange, PATIENT_MIN_DATE]);

  const adjustedDateRange = useMemo(() => getAdjustedDateRange(dateRange), [getAdjustedDateRange, dateRange]);

  // Use progressive loading for patient data because it's a large dataset
  const {
    data: patientData,
    loading: patientLoading,
    progress,
    currentChunk,
  } = ProgressiveLoading({
    dateRange: adjustedDateRange,
    query: GET_PATIENT_REPORT_QUERY,
    dataExtractor: useCallback((data: any) => data?.allPatients || [], []),
    chunkLimit: 1000,
    enabled: areDatesValid(adjustedDateRange),
  });

  const { data: diagnosisData, loading: diagnosisLoading } = useQuery(GET_DIAGNOSIS_REPORT_QUERY, {
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
    skip: !areDatesValid(dateRange),
    variables: {
      onOrAfterDate: dateRange[0]?.format('YYYY-MM-DD'),
      onOrBeforeDate: dateRange[1]?.format('YYYY-MM-DD'),
      insightsRequest: true,
      rowsPerPage: 9999,
      status: ['Prescription'],
      onlyForRequestingPractitioner: false,
    },
  });

  const reportData = useMemo(() => {
    // For background loading, only return empty if we're still loading the initial request
    // This prevents data flickering when switching between report types
    if (
      (reportType === ReportType.PATIENT && patientLoading && patientData.length === 0) ||
      (reportType === ReportType.DIAGNOSIS && diagnosisLoading && !diagnosisData) ||
      (reportType === ReportType.PRESCRIPTION && prescriptionsLoading)
    ) {
      return [];
    }

    let data = filterDataByReportType(
      reportType,
      rawCareplanData,
      patientData?.length ? { allPatients: patientData } : null,
      diagnosisData,
    );

    data = applyDateFilter(data, adjustedDateRange);

    // Apply physician exclusion filtering for diagnosis data
    if (reportType === ReportType.DIAGNOSIS && tenantConfig?.insightsExcludePhysicians) {
      data = data.filter(
        (record: any) =>
          !tenantConfig.insightsExcludePhysicians.some(
            (excluded) => excluded.toLowerCase() === record.createdBy?.toLowerCase(),
          ),
      );
    }

    return reportType === ReportType.PRESCRIPTION ? expandSiteGroupData(data) : data;
  }, [
    rawCareplanData,
    patientData,
    diagnosisData,
    reportType,
    adjustedDateRange,
    patientLoading,
    diagnosisLoading,
    prescriptionsLoading,
    tenantConfig,
  ]);

  // Generate dynamic columns based on the processed data
  const columns: GridColDef[] = useMemo(() => {
    return generateColumns(reportData, reportType);
  }, [reportData, reportType]);

  const handleRunReport = useCallback(() => {
    if (reportData.length === 0 || columns.length === 0) return;

    const visibleColumns = apiRef.current?.getVisibleColumns() || columns;

    // Check for active filters
    const filterModel = apiRef.current?.state?.filter?.filterModel;
    const hasActiveFilters =
      filterModel?.items?.length || (filterModel?.quickFilterValues && filterModel.quickFilterValues.length > 0);

    let filteredAndSortedData = reportData;

    if (hasActiveFilters) {
      // Get the filtered row lookup from the DataGrid state
      const filteredRowsLookup = apiRef.current?.state?.filter?.filteredRowsLookup;
      if (filteredRowsLookup) {
        filteredAndSortedData = reportData.filter(
          (row: any) => filteredRowsLookup[row.id || row.uuid || `row_${reportData.indexOf(row)}`],
        );

        // Apply sorting to the filtered data
        const sortModel = apiRef.current?.state.sorting?.sortModel?.[0];
        if (sortModel) {
          filteredAndSortedData = applySorting(filteredAndSortedData, sortModel);
        }
      }
    } else {
      // No filters active - use DataGrid's built-in sorted data
      filteredAndSortedData = apiRef.current?.getSortedRows() || reportData;
    }

    generateCSVReport(reportType, visibleColumns, filteredAndSortedData);
  }, [reportData, columns, reportType, apiRef]);

  // Memoize the date range change handler to prevent DateRangePicker re-renders
  const handleDateRangeChange = useCallback(
    (newValue: [Dayjs | null, Dayjs | null]) => {
      setDateRange([
        newValue[0]?.isValid() ? newValue[0] : dateRange[0],
        newValue[1]?.isValid() ? newValue[1] : dateRange[1],
      ]);
    },
    [dateRange],
  );

  const handleReportTypeChange = useCallback((newReportType: ReportType) => {
    setReportType(newReportType);
    setPaginationModel({ page: 0, pageSize: 20 });
    apiRef.current?.setQuickFilterValues([]);
  }, []);

  const isLoading =
    (patientLoading && reportType === ReportType.PATIENT && patientData.length === 0) ||
    (diagnosisLoading && reportType === ReportType.DIAGNOSIS && !diagnosisData) ||
    (prescriptionsLoading && reportType === ReportType.PRESCRIPTION);

  useEffect(() => {
    if (
      userData &&
      !userData.permissions?.includes(PermissionType.INSIGHTS_SUPERUSER_ACCESS) &&
      !isDemo &&
      !LOCAL_ENV
    ) {
      history.replace('/error');
    }
  }, [userData, history]);

  return (
    <Stack direction="column" minHeight="100%" spacing={1} sx={{ paddingLeft: '40px', paddingRight: '40px' }}>
      <Typography variant="h5" sx={{ marginRight: '10px', mt: 4, mb: 2 }}>
        Reports
      </Typography>
      <Paper
        elevation={0}
        sx={{ display: 'flex', flexDirection: 'column', marginTop: '20px', padding: '16px 0', minHeight: 0 }}>
        <Stack
          direction="row"
          spacing={2}
          sx={{ marginBottom: '16px' }}
          justifyContent="flex-start"
          alignItems="center">
          <DateRangePicker
            id="dateRange"
            value={dateRange}
            onChange={handleDateRangeChange}
            sx={{ width: '280px' }}
            dateFormat={CurrentAppConfig.Insights?.chartDateFormat}
          />
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Report Type</InputLabel>
            <Select
              value={reportType}
              label="Report Type"
              onChange={(e) => handleReportTypeChange(e.target.value as ReportType)}>
              <MenuItem value={ReportType.PRESCRIPTION}>Prescription</MenuItem>
              <MenuItem value={ReportType.DIAGNOSIS}>Diagnosis</MenuItem>
              <MenuItem value={ReportType.PATIENT}>Patient</MenuItem>
            </Select>
          </FormControl>
          {reportType === ReportType.PATIENT && patientLoading && progress < 100 && (
            <Box sx={{ width: '600px', ml: 2 }}>
              <LinearProgress variant={'determinate'} value={progress} />
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 0.5 }}>
                {currentChunk > 0 ? `Loading data... (${patientData.length} records loaded)` : ''}
              </Typography>
            </Box>
          )}
        </Stack>
        <Stack sx={{ flex: 1, minHeight: '65vh', maxHeight: '65vh' }}>
          <DataGrid
            apiRef={apiRef}
            rows={reportData}
            columns={columns}
            getRowId={(row: any) => row.id || row.uuid || `row_${reportData.indexOf(row)}`}
            pagination
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[20, 50, 100]}
            slots={{
              toolbar: CustomToolbar,
            }}
            hideFooterSelectedRowCount
            loading={isLoading}
            sx={dataGridStyles}
          />
        </Stack>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ marginTop: '16px', flexShrink: 0, height: '36px' }}>
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', height: '100%' }}>
            {shouldShowPatientDateWarning && (
              <Alert
                severity="warning"
                sx={{ maxWidth: '600px', margin: 0, padding: '4px 12px', height: 'fit-content' }}>
                Patient data is only available from{' '}
                {PATIENT_MIN_DATE?.format(CurrentAppConfig.Insights?.chartDateFormat || 'DD/MM/YYYY') || 'loading...'}{' '}
                onwards
              </Alert>
            )}
          </Box>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => navToPage('index')}
              sx={{ textTransform: 'none' }}>
              Back
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleRunReport}
              disabled={
                reportData.length === 0 ||
                (reportType === ReportType.PATIENT && patientLoading && progress < 100) ||
                (reportType === ReportType.DIAGNOSIS && diagnosisLoading) ||
                (reportType === ReportType.PRESCRIPTION && prescriptionsLoading)
              }
              sx={{ textTransform: 'none' }}>
              Generate Report
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
};

export default Reports;
